import React, { Component, ReactNode } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { Home as HomeIcon, Refresh as RefreshIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallbackRoutes?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: any;
}

class RoutingErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('RoutingErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleNavigateHome = () => {
    // Force navigate to home with default language
    window.location.href = '/es/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            padding: 3,
            textAlign: 'center'
          }}
        >
          <Alert severity="error" sx={{ mb: 3, maxWidth: '600px' }}>
            <Typography variant="h6" gutterBottom>
              Error de Navegación / Navigation Error
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Hubo un problema cargando la página. Esto puede deberse a un error en la 
              carga de traducciones o problemas de conectividad.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There was a problem loading the page. This may be due to translation 
              loading errors or connectivity issues.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleReload}
              size="large"
            >
              Recargar / Reload
            </Button>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={this.handleNavigateHome}
              size="large"
            >
              Ir al Inicio / Go Home
            </Button>
          </Box>

          {this.props.fallbackRoutes && (
            <Box sx={{ mt: 4, width: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Navegación de Emergencia / Emergency Navigation
              </Typography>
              {this.props.fallbackRoutes}
            </Box>
          )}

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box sx={{ mt: 4, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, maxWidth: '800px' }}>
              <Typography variant="subtitle2" gutterBottom>
                Development Error Details:
              </Typography>
              <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default RoutingErrorBoundary;